const NPM_REGISTRY = "https://registry.npmjs.org";

/**
 * Metadata for a package from the npm registry. Only the fields we use are typed.
 *
 * @see https://github.com/npm/registry/blob/master/docs/responses/package-metadata.md
 */
interface NpmPackageMetadata {
  name?: string;
  "dist-tags"?: { latest?: string };
  time?: Record<string, string>;
  versions?: Record<string, unknown>;
}

/**
 * Returns true if the version has a semver prerelease segment (e.g. -canary, -alpha, -beta).
 * Full releases like 1.2.3 or 1.2.3+build.1 return false.
 */
function isPrerelease(version: string): boolean {
  const v = version.replace(/^v/, "");
  return /^\d+\.\d+\.\d+-/.test(v);
}

/**
 * Compares two semver-like version strings (e.g. "1.2.3").
 * Returns negative if `a < b`, positive if `a > b`, 0 if equal.
 * Prerelease segments are treated as less than release.
 */
function compareVersions(a: string, b: string): number {
  const parse = (v: string): number[] => {
    const parts = v.replace(/^v/, "").split(/[-+]/)[0].split(".");
    return parts.map((p) => {
      const n = parseInt(p, 10);
      return Number.isNaN(n) ? 0 : n;
    });
  };
  const pa = parse(a);
  const pb = parse(b);
  const len = Math.max(pa.length, pb.length);
  for (let i = 0; i < len; i++) {
    const na = pa[i] ?? 0;
    const nb = pb[i] ?? 0;
    if (na !== nb) return na - nb;
  }
  return 0;
}

/**
 * Returns the latest full-release version of an npm package that has been
 * published for at least `minimumReleaseAgeMinutes` minutes. Prefers the
 * version indicated by the registry's "latest" dist-tag when it is a full
 * release: if that version's publish time is in the registry and old enough,
 * it is used; if it is not in the registry's "time" object (e.g. truncated
 * for large packages), the dist-tag is still used so npm's "latest" is
 * followed. When the dist-tag version is too new (in time but not old enough),
 * we fall back to the highest full-release version that is old enough and
 * ≤ latest (e.g. latest 2.x that's old enough, not 3.0.0). Prerelease
 * versions are excluded. Returns null if `time` is missing or no version is
 * eligible.
 *
 * @param packageName - npm package name (e.g. "projen").
 * @param minimumReleaseAgeMinutes - minimum age in minutes (e.g. 1440 for 24h).
 * @returns The latest eligible version string, or null.
 */
export async function getLatestEligibleVersion(
  packageName: string,
  minimumReleaseAgeMinutes: number,
): Promise<string | null> {
  const url = `${NPM_REGISTRY}/${encodeURIComponent(packageName)}`;
  let res: Response;
  try {
    res = await fetch(url, {
      headers: { Accept: "application/json" },
    });
  } catch {
    return null;
  }
  if (!res.ok) return null;
  let data: NpmPackageMetadata;
  try {
    data = (await res.json()) as NpmPackageMetadata;
  } catch {
    return null;
  }
  const time = data.time;
  if (!time || typeof time !== "object") return null;

  const nowMs = Date.now();
  const minAgeMs = minimumReleaseAgeMinutes * 60 * 1000;

  const distTagLatest = data["dist-tags"]?.latest;
  if (distTagLatest && !isPrerelease(distTagLatest)) {
    const publishedAtStr = time[distTagLatest];
    if (typeof publishedAtStr === "string") {
      const publishedAt = Date.parse(publishedAtStr);
      if (!Number.isNaN(publishedAt) && nowMs - publishedAt >= minAgeMs) {
        return distTagLatest;
      }
    } else {
      return distTagLatest;
    }
  }

  const versionTimestamps: { version: string; publishedAt: number }[] = [];
  for (const [key, value] of Object.entries(time)) {
    if (key === "created" || key === "modified" || typeof value !== "string") {
      continue;
    }
    if (isPrerelease(key)) continue;
    const publishedAt = Date.parse(value);
    if (Number.isNaN(publishedAt)) continue;
    const ageMs = nowMs - publishedAt;
    if (ageMs >= minAgeMs) {
      versionTimestamps.push({ version: key, publishedAt });
    }
  }

  if (versionTimestamps.length === 0) return null;

  let candidates = versionTimestamps;
  if (distTagLatest && !isPrerelease(distTagLatest)) {
    candidates = versionTimestamps.filter(
      (e) => compareVersions(e.version, distTagLatest) <= 0,
    );
  }
  if (candidates.length === 0) return null;

  candidates.sort((a, b) => compareVersions(b.version, a.version));
  return candidates[0].version;
}
